import { IllustrationOpeningAnimation } from "@assets/IllustrationOpeningAnimation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { OpeningOverlay } from "~/components/lock/MyLocks/OpeningOverlay";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";

// Fallback duration used only when we don't have an unlock request id to poll
// (the backend may not return it to a grant holder). When we do have one we
// poll the unlock request until the space reports CLOSED again.
const OPENING_ANIMATION_MS = 2500;

export const Route = createFileRoute("/$hubMetaName/swap/success")({
	beforeLoad: () => {
		document.title = "bacehub | Lock";
	},
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			// Present when utilizationGrantUse returned the resulting unlock
			// request id. When set we poll its status instead of using the timer.
			unlockRequestId: z.string().optional(),
		})
	),
});

function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const { hubMetaName } = Route.useParams();
	const { unlockRequestId } = Route.useSearch();

	const [phase, setPhase] = useState<"opening" | "done">("opening");

	// Once the door cycle is finished, take the user to their locks.
	const goToMyLocks = () => navigate({ to: "/$hubMetaName/swap/my", params: { hubMetaName } });

	// When we don't have an unlock request id we can't poll, so fall back to an
	// optimistic timer. When we do have one, the polling component drives the
	// transition via onTerminate.
	useEffect(() => {
		if (unlockRequestId) return;
		const timer = setTimeout(() => setPhase("done"), OPENING_ANIMATION_MS);
		return () => clearTimeout(timer);
	}, [unlockRequestId]);

	if (phase === "opening" && unlockRequestId) {
		return (
			<OpeningOverlay
				service="LOCK_B2B"
				unlockRequestId={unlockRequestId}
				onTerminate={(outcome) => {
					if (outcome === "success") {
						goToMyLocks();
						return;
					}
					setPhase("done");
				}}
			/>
		);
	}

	if (phase === "done") {
		return (
			<PageContainer
				footer={
					<div className="flex flex-col gap-1">
						<Button size="lg" className="w-full" asChild>
							<Link to="/" search={{ hubMetaName }}>
								<T>{i18n.BACK_TO_HOME()}</T>
							</Link>
						</Button>
						<Button size="lg" variant="ghost" className="w-full">
							<TriangleAlert className="size-4" />
							<T>{i18n.LOCK_OPENED_PROBLEM()}</T>
						</Button>
					</div>
				}
			>
				<AppHeader service="LOCK_B2B" showHelpButton={true} />
				<div className="flex flex-col gap-6 px-6 pt-6">
					<div className="flex flex-col gap-2">
						<h1 className="text-2xl font-breit font-medium">
							<T>{i18n.LOCK_OPENED_TITLE()}</T>
						</h1>
						<p className="text-base text-muted-foreground">
							<T>{i18n.LOCK_OPENED_DESC()}</T>
						</p>
					</div>
					<div className="flex items-center justify-center pt-4">
						<IllustrationOpeningAnimation className="w-[200px]" />
					</div>
				</div>
			</PageContainer>
		);
	}

	return (
		<PageContainer className="px-6">
			<div className="-mx-6">
				<AppHeader service="LOCK_B2B" showHelpButton={true} />
			</div>
			<h1 className="text-2xl font-breit">
				<T>{i18n.OPENING_DOOR_FOR_YOU()}</T>
			</h1>
			<p className="text-sm text-muted-foreground">
				<T>{i18n.OPENING_DOOR_DESC()}</T>
			</p>
			<div className="flex h-[468px] items-center justify-center">
				<IllustrationOpeningAnimation className="w-[163px]" />
			</div>
		</PageContainer>
	);
}
